import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import { CalendarTwoIcon, PdfIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal from '@repo/ui/modal'
import Rating from '@repo/ui/rating'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { getAffectedStaffEndOfStayReport } from '../../../../api/affected-staffs/get-affected-end-of-stay-report'
import { getAffectedStaff } from '../../../../api/affected-staffs/get-affected-staff'
import { API } from '../../../../api/apis.config'
import { getAffectedStaffYouthsRating } from '../../../../api/end-of-journey-reviews/staff/get-affected-staff-youths-rating'
import { useObserver } from '../../../../lib/contexts/observer'
import useAuth from '../../../../lib/hooks/use-auth'
import { useDownloadDocuments } from '../../../../lib/hooks/use-download-documents'
import useTranslate from '../../../../lib/hooks/use-translate'
import { RoleEnum } from '../../../../lib/types/enums/users/role.enum'
import { AffectedStaffInput } from '../../../../lib/types/models/affectedStaff/affected-staff-input'
import { StaffRequestProperties } from '../../../../lib/types/models/staff-request/properties.staff-request'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { extractErrorMessage } from '../../../../lib/utils/functions/misc.functions'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { DPAESection } from '../../../elements/dpae-section'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import LoadingScreen from '../../../elements/loading/loading-screen'
import ValidationButtons from '../../../elements/validation-buttons'
import { formatReviewNumber } from '../../ols/centers/center-tab-views/center-end-of-stay-appreciations/review-rating-pill'
import LabelWithText from '../../ols/label-with-text'
import { updateAffectedStaffSchema } from '../../staff-requests/utils'
import AffectedStaffContractSummarySection from '../affected-staff-contract-summary-section'
import AffectedStaffJourneyInformation from '../affected-staff-journey-information'
import AffectedStaffPayscale from '../affected-staff-payscale'
import AffectedStaffRad from '../affected-staff-rad'
import AffectedStaffTravelExpenses from '../affected-staff-travel-expenses'
import AffectedStaffTripInformation from '../affected-staff-trip-information'
import AffectedStaffViewHeader from '../affected-staff-view-header'

type Props = BaseFormProps<AffectedStaffInput, true> & {
  affectedStaffId: number
}

const AffectedStaffViewForm = (props: Props) => {
  const { affectedStaffId, defaultValues } = props
  const { t } = useTranslate()
  const { userRole } = useAuth()
  const { downloadDocuments } = useDownloadDocuments()
  const [reportPreviewUrl, setReportPreviewUrl] = useState<string>()

  const { data: report } = useQuery({
    queryKey: ['affected-staff-end-of-stay-report', affectedStaffId],
    queryFn: () => {
      if (affectedStaffId) return getAffectedStaffEndOfStayReport(affectedStaffId)
    }
  })

  // "Note moyenne des jeunes": LEC only, supervisors never see it
  const { data: youthsRating, error: youthsRatingError } = useQuery({
    queryKey: ['affected-staff-youths-rating', affectedStaffId],
    queryFn: () => getAffectedStaffYouthsRating(affectedStaffId),
    enabled: Boolean(affectedStaffId) && userRole === RoleEnum.ADMIN
  })

  const { data, refetch } = useQuery({
    queryKey: ['affected-staffs', affectedStaffId],
    queryFn: () => {
      if (affectedStaffId) return getAffectedStaff(affectedStaffId, 'FULL')
    },
    enabled: Boolean(affectedStaffId)
  })

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      downloadDocuments(
        {
          endpoint: API.affectedStaffs.generateEndOfStayReportPdf.url.replace(
            ':id',
            affectedStaffId.toString()
          )
        },
        { automaticallyDownload: false }
      ),
    onSuccess: ({ fileLink }) => setReportPreviewUrl(fileLink)
  })

  function closeReportPreview() {
    if (reportPreviewUrl) URL.revokeObjectURL(reportPreviewUrl)
    setReportPreviewUrl(undefined)
  }

  useObserver({
    key: 'update-affected-staff-data',
    update: refetch
  })
  const staffRequest = data?.staffRequest
  const staff = data?.staff

  const isForeignStaff = staffRequest && StaffRequestProperties.isForeignStaff(staffRequest)
  const form = useForm<AffectedStaffInput>({
    resolver: zodResolver(updateAffectedStaffSchema(t)),
    defaultValues,
    mode: FORM_MODE
  })

  const endOfStayReportValidatedAt = form.watch('endOfStayReportValidatedAt')

  if (!staffRequest || !staff) return <LoadingScreen />
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(props.buttonsConfig))}>
        <div>
          <AffectedStaffViewHeader staff={staff} staffRequest={staffRequest} />
          <div className="h-4" />
          <div className={'flex w-full  gap-3'}>
            <div className="flex flex-col w-full gap-3">
              <AffectedStaffPayscale
                title="Rémunération"
                readonly={!!data.contract?.contractSignedAt}
                staffId={staff.id}
                staffRequestId={staffRequest.id}
                additionalComponent={
                  <div className="grid grid-cols-3 gap-4">
                    <LabelWithText
                      label="Montant du PAS"
                      data={
                        <Badge
                          text={valueRender(data.pas, (value) =>
                            NumberFormatter.formatCurrency(value, data.currency)
                          )}
                          variant="primaryLight"
                        />
                      }
                    />

                    <LabelWithText
                      label="Virement fait le"
                      data={
                        <Badge
                          text={valueRender(data.paymentValidatedAt, (value) =>
                            DateFormatter.DDMMYYYY(value)
                          )}
                        />
                      }
                    />
                  </div>
                }
              />
              <AffectedStaffContractSummarySection affectedStaff={data} />
              {!isForeignStaff && <AffectedStaffRad mode="view" />}
              <DPAESection
                id={affectedStaffId}
                dpaeNumber={data.dpaeNumber ?? '-'}
                endpoint={API.affectedStaffs.exportDpaeXml.url}
                dpaeDownloadAt={data.dpaeDownloadAt}
                dpaeFilledAt={data.dpaeFilledAt}
                notApplicable={isForeignStaff}
                onDownloadDpaeXml={() => refetch()}
              />
              <AffectedStaffTravelExpenses affectedStaff={data} />
            </div>
            <div className="flex flex-col w-full gap-3">
              <AffectedStaffJourneyInformation staffRequest={staffRequest} />
              <AffectedStaffTripInformation affectedStaff={data} />
              <InnerBox className="rounded-lg ">
                <div className="flex justify-between items-center w-full">
                  <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
                    Évaluations
                  </Typography>
                  {!!report && (
                    <Button
                      variant="secondary"
                      type="button"
                      label="Voir le rapport"
                      icon={PdfIcon}
                      loading={isPending}
                      onClick={() => {
                        mutate()
                      }}
                    />
                  )}
                </div>
                <div className="h-3" />
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={form.watch('sentEndOfStayReport')}
                    onChange={(checked) => form.setValue('sentEndOfStayReport', checked)}
                  />
                  <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                    Sacoche / rapport traités
                  </Typography>
                  {endOfStayReportValidatedAt && (
                    <Badge
                      icon={<CalendarTwoIcon width="16px" height="16px" />}
                      iconPosition="start"
                      text={`Traité le ${DateFormatter.DDMMYYYY(endOfStayReportValidatedAt)}`}
                    />
                  )}
                </div>
                {(youthsRating || youthsRatingError) && (
                  <>
                    <div className="h-3.5" />
                    <div className="flex items-center gap-2">
                      <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                        {t('affected.staff.evaluations.youths.rating')}
                      </Typography>
                      {youthsRatingError ? (
                        // e.g. a mixed-scale average refused by the backend
                        <Typography variant="label-medium" color={tailwindColors.red.DEFAULT}>
                          {extractErrorMessage(youthsRatingError) ??
                            t('affected.staff.evaluations.youths.rating.error')}
                        </Typography>
                      ) : youthsRating &&
                        youthsRating.average !== null &&
                        youthsRating.scaleMax !== null ? (
                        <>
                          <Rating
                            key={`${youthsRating.average}/${youthsRating.scaleMax}`}
                            mode="view"
                            max={youthsRating.scaleMax}
                            defaultValue={youthsRating.average}
                            formatValue={formatReviewNumber}
                          />
                          <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                            {t('affected.staff.evaluations.youths.rating.answers', {
                              answers: youthsRating.answerCount
                            })}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
                          {youthsRating?.rated
                            ? '-'
                            : t('affected.staff.evaluations.youths.rating.not.rated')}
                        </Typography>
                      )}
                    </div>
                  </>
                )}
                <div className="h-3.5" />
                <div className="flex items-center gap-2">
                  <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                    {t('affected.staff.evaluations.lec.rating')}
                  </Typography>
                  <Rating
                    mode="edit"
                    precision={1}
                    defaultValue={form.watch('rating') ?? 0}
                    // clicking the selected star clears the rating
                    onChange={(value) => form.setValue('rating', value || null)}
                  />
                </div>
                <div className="h-3" />
                <FormField
                  control={form.control}
                  name="comment"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledInput
                          {...field}
                          value={field.value ?? ''}
                          type="textarea"
                          rows={3}
                          label={t('affected.staff.evaluations.comment')}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </InnerBox>
            </div>
          </div>

          <div className="pb-6 px-4">
            <ValidationButtons {...props} />
          </div>

          <Modal
            isOpen={!!reportPreviewUrl}
            title="Rapport de fin de séjour"
            icon={PdfIcon}
            size="large"
            hideActions
            onCancel={closeReportPreview}
          >
            <iframe
              src={reportPreviewUrl}
              width="100%"
              height="600px"
              title="Rapport de fin de séjour"
            />
          </Modal>
        </div>
      </form>
    </Form>
  )
}
export default AffectedStaffViewForm
